import { useEffect, useMemo, useState } from 'react';
import './App.css';
import {
  ALL_SERVICES,
  EVAL_DATA,
  NO_INTERVENTION_REASONS,
  STATUS_OPTIONS,
  UNABLE_REASONS,
  createId,
  getRecommendedServices,
  getRecommendationSet,
} from './data';
import { api, reviewSentence } from './mockApi';

const emptySubject = { managementNo: '', name: '' };

function maskName(name) {
  if (!name) return '';
  if (name.length === 1) return name;
  if (name.length === 2) return `${name[0]}○`;
  return `${name[0]}${'○'.repeat(name.length - 2)}${name.at(-1)}`;
}
function makeInitialSections() {
  return Object.fromEntries(EVAL_DATA.map((item) => [item.id, {
    status: '',
    unableReason: '',
    selectedProblems: {},
  }]));
}

function makeProblemState() {
  return { intervention: 'yes', noReason: '', services: [] };
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

function makeService(serviceCode, problemName) {
  const { goals, details, pairs = [] } = getRecommendationSet(serviceCode, problemName);
  const firstGoal = goals[0] || '';
  const matchingDetails = unique(pairs.filter((pair) => pair.goal === firstGoal).map((pair) => pair.detail));
  const detailOptions = matchingDetails.length > 0 ? matchingDetails : details;
  const firstDetail = detailOptions[0] || '';
  return {
    id: createId('svc'),
    code: serviceCode,
    goalSource: firstGoal,
    goal: firstGoal,
    goalOptions: goals,
    recommendationPairs: pairs,
    allDetailOptions: details,
    detailOptions,
    details: [{
      id: createId('detail'),
      source: firstDetail ? 'recommended' : 'manual',
      sourceText: firstDetail,
      text: firstDetail,
    }],
  };
}

function countServices(sections) {
  return EVAL_DATA.reduce((total, item) => {
    const section = sections[item.id];
    if (!section || section.status !== 'issue') return total;
    return total + Object.values(section.selectedProblems || {}).reduce((subtotal, problem) => subtotal + (problem.services?.length || 0), 0);
  }, 0);
}

function Login({ onLogin }) {
  const [name, setName] = useState('');
  const [institution, setInstitution] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      setError('');
      await onLogin({ name, institution });
    } catch (e) {
      setError(e.message || '로그인 정보를 확인해 주세요.');
    } finally {
      setLoading(false);
    }
  };

  return <main className="login-shell">
    <section className="login-card">
      <p className="eyebrow">충청남도광역치매센터</p>
      <h1>맞춤형 사례관리 지원 시스템</h1>
      <p className="muted">대상자 평가 결과에 따라 서비스와 실행 문구를 추천하는 업무 지원 시제품입니다.</p>
      <form onSubmit={submit}>
        <label>담당자 성명<input value={name} onChange={(e) => setName(e.target.value)} placeholder="홍길동" autoComplete="name" /></label>
        <label>소속 기관<input value={institution} onChange={(e) => setInstitution(e.target.value)} placeholder="○○시 치매안심센터" /></label>
        {error && <p className="error-text" role="alert">{error}</p>}
        <button className="primary wide" disabled={loading}>{loading ? '접속 중…' : '시스템 접속'}</button>
      </form>
      <p className="privacy-note">※ 현재는 서버 연동 전 시제품입니다. 실제 주민등록번호, 연락처 등 민감정보는 입력하지 마세요.</p>
    </section>
  </main>;
}

function StatusSelector({ value, onChange, required }) {
  return <div className="status-row" role="group" aria-label="평가 상태">
    {STATUS_OPTIONS.map((option) => <button
      type="button"
      className={`status-chip ${value === option.value ? 'active' : ''}`}
      aria-pressed={value === option.value}
      key={option.value}
      onClick={() => onChange(option.value)}
    >{option.label}</button>)}
    {!required && !value && <span className="optional-hint">선택영역은 미선택 가능</span>}
  </div>;
}

function ReviewBox({ type, text, service, problem, onApply }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const run = async () => {
    setLoading(true);
    setError('');
    try {
      setResult(await reviewSentence({ type, text, service, problem }));
    } catch (e) {
      setError('문장 검토에 실패했습니다. 작성한 문장은 그대로 저장할 수 있습니다.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="review-box">
    <button type="button" className="ghost small" onClick={run} disabled={!text.trim() || loading}>
      {loading ? '검토 중…' : '문장 검토·다듬기'}
    </button>
    {error && <p className="inline-error">{error}</p>}
    {result && <div className="review-result">
      {result.warnings.length > 0 && <ul>{result.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>}
      <p><strong>제안 문장</strong><br />{result.suggestion}</p>
      <button type="button" className="secondary small" onClick={() => onApply(result.suggestion)}>제안문 반영</button>
    </div>}
  </div>;
}

function ServiceEditor({ service, problem, onChange, onRemove }) {
  const [expanded, setExpanded] = useState(true);
  const update = (patch) => onChange({ ...service, ...patch });
  const goalOptions = service.goalOptions || [service.goal].filter(Boolean);
  const recommendationPairs = service.recommendationPairs || [];
  const allDetailOptions = service.allDetailOptions || service.detailOptions || [];
  const detailOptions = service.detailOptions || allDetailOptions;

  const changeGoal = (goal) => {
    const paired = unique(recommendationPairs.filter((pair) => pair.goal === goal).map((pair) => pair.detail));
    const nextOptions = paired.length > 0 ? paired : allDetailOptions;
    const firstSuggestion = nextOptions[0] || '';
    const nextDetails = service.details.map((detail, index) => {
      if (index !== 0 || (detail.text && detail.text !== detail.sourceText)) return detail;
      return { ...detail, source: firstSuggestion ? 'recommended' : 'manual', sourceText: firstSuggestion, text: firstSuggestion };
    });
    update({ goalSource: goal, goal, detailOptions: nextOptions, details: nextDetails });
  };

  const addDetail = () => {
    const used = new Set(service.details.map((detail) => detail.sourceText));
    const suggestion = detailOptions.find((detail) => !used.has(detail)) || '';
    update({
      details: [...service.details, {
        id: createId('detail'),
        source: suggestion ? 'recommended' : 'manual',
        sourceText: suggestion,
        text: suggestion,
      }],
    });
  };
  const updateDetail = (id, patch) => update({
    details: service.details.map((detail) => detail.id === id ? { ...detail, ...patch } : detail),
  });
  const selectDetail = (id, suggestion) => updateDetail(id, suggestion
    ? { source: 'recommended', sourceText: suggestion, text: suggestion }
    : { source: 'manual', sourceText: '' });
  const removeDetail = (id) => update({ details: service.details.filter((detail) => detail.id !== id) });

  return <article className="service-card" id={`service-${service.id}`}>
    <header className="service-head">
      <button type="button" className="collapse" aria-label={expanded ? '서비스 접기' : '서비스 펼치기'} onClick={() => setExpanded(!expanded)}>{expanded ? '▼' : '▶'}</button>
      <div><span className="mini-label">선택 서비스</span><strong>{service.code}</strong></div>
      <button type="button" className="danger-link" onClick={onRemove}>서비스 삭제</button>
    </header>
    {expanded && <div className="service-body">
      <section>
        <div className="field-title">추천 목표</div>
        <p className="field-help">선택한 문제와 서비스에 맞는 목표와 세부내용 추천입니다.</p>
        <select value={service.goalSource || service.goal} onChange={(e) => changeGoal(e.target.value)}>
          {goalOptions.map((goal) => <option value={goal} key={goal}>{goal}</option>)}
        </select>
        <textarea value={service.goal} onChange={(e) => update({ goal: e.target.value })} rows={3} aria-label={`${service.code} 목표`} />
        {service.goalSource && service.goal !== service.goalSource && <details><summary>추천 원문 보기</summary><p>{service.goalSource}</p></details>}
        <ReviewBox type="goal" text={service.goal} service={service.code} problem={problem} onApply={(goal) => update({ goal })} />
      </section>
      <section>
        <div className="field-title spread"><span>세부내용</span><button type="button" className="secondary small" onClick={addDetail}>+ 세부내용 추가</button></div>
        {service.details.map((detail, index) => <div className="detail-card" id={`detail-${detail.id}`} key={detail.id}>
          <div className="detail-head"><strong>세부내용 {index + 1}</strong></div>
          {detailOptions.length > 0 && <label className="recommend-select">추천 세부내용
            <select value={detail.sourceText || ''} onChange={(e) => selectDetail(detail.id, e.target.value)}>
              <option value="">직접 작성</option>
              {detailOptions.map((option) => <option value={option} key={option}>{option}</option>)}
            </select>
          </label>}
          <textarea rows={3} value={detail.text} onChange={(e) => updateDetail(detail.id, { text: e.target.value })} placeholder="대상·행동·점검 내용을 작성하세요." aria-label={`${service.code} 세부내용 ${index + 1}`} />
          {detail.sourceText && detail.text !== detail.sourceText && <details><summary>추천 원문 보기</summary><p>{detail.sourceText}</p></details>}
          <ReviewBox type="detail" text={detail.text} service={service.code} problem={problem} onApply={(text) => updateDetail(detail.id, { text })} />
          {service.details.length > 1 && <button type="button" className="danger-link" onClick={() => removeDetail(detail.id)}>이 세부내용 삭제</button>}
        </div>)}
      </section>
    </div>}
  </article>;
}

function ProblemEditor({ evalId, name, state, onChange }) {
  const [query, setQuery] = useState('');
  const recommended = getRecommendedServices(evalId, name);
  const allOptions = useMemo(() => ALL_SERVICES
    .filter(({ code, group }) => `${code} ${group}`.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 60), [query]);
  const selectedCodes = new Set(state.services.map((service) => service.code));
  const addService = (code) => {
    if (!code || selectedCodes.has(code)) return;
    onChange({ ...state, services: [...state.services, makeService(code, name)] });
  };

  return <div className="problem-editor">
    <div className="intervention-row" role="group" aria-label={`${name} 개입 여부`}>
      <label><input type="radio" checked={state.intervention === 'yes'} onChange={() => onChange({ ...state, intervention: 'yes' })} /> 개입</label>
      <label><input type="radio" checked={state.intervention === 'no'} onChange={() => onChange({ ...state, intervention: 'no' })} /> 미개입</label>
    </div>
    {state.intervention === 'no' ? <label>미개입 사유(선택)
      <select value={state.noReason} onChange={(e) => onChange({ ...state, noReason: e.target.value })}>
        <option value="">선택하지 않음</option>{NO_INTERVENTION_REASONS.map((reason) => <option key={reason}>{reason}</option>)}
      </select>
    </label> : <>
      <div className="recommendation-panel">
        <p className="mini-label">문제항목별 대표 추천 서비스</p>
        {recommended.map((code, index) => <button type="button" className={`recommendation ${selectedCodes.has(code) ? 'selected' : ''}`} disabled={selectedCodes.has(code)} key={code} onClick={() => addService(code)}>
          {index === 0 && <span className="top-tag">대표</span>}{code}{selectedCodes.has(code) && ' · 추가됨'}
        </button>)}
        <details><summary>전체 서비스 검색</summary>
          <input className="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="서비스명 또는 코드 검색" />
          <div className="service-search-list">{allOptions.map(({ code, group }) => <button type="button" disabled={selectedCodes.has(code)} key={code} onClick={() => addService(code)}><small>{group}</small>{code}</button>)}</div>
        </details>
      </div>
      {state.services.map((service) => <ServiceEditor
        key={service.id}
        service={service}
        problem={name}
        onChange={(next) => onChange({ ...state, services: state.services.map((item) => item.id === service.id ? next : item) })}
        onRemove={() => onChange({ ...state, services: state.services.filter((item) => item.id !== service.id) })}
      />)}
    </>}
  </div>;
}

function EvaluationSection({ item, value, onChange }) {
  const updateProblem = (problem, next) => onChange({ ...value, selectedProblems: { ...value.selectedProblems, [problem]: next } });
  const toggleProblem = (problem, checked) => {
    const next = { ...value.selectedProblems };
    if (checked) next[problem] = next[problem] || makeProblemState();
    else delete next[problem];
    onChange({ ...value, selectedProblems: next });
  };
  const updateStatus = (status) => onChange({
    ...value,
    status,
    unableReason: status === 'unable' ? value.unableReason : '',
    selectedProblems: status === 'issue' ? value.selectedProblems : {},
  });

  return <section className="eval-section" id={`section-${item.id}`}>
    <header className="section-heading"><div><span className="section-number">{item.id}</span><h2>{item.name}</h2></div><span className={item.req ? 'required' : 'optional'}>{item.req ? '필수' : '선택'}</span></header>
    <StatusSelector value={value.status} onChange={updateStatus} required={item.req} />
    {value.status === 'unable' && <label>평가 불가 사유(선택)
      <select value={value.unableReason} onChange={(e) => onChange({ ...value, unableReason: e.target.value })}>
        <option value="">선택하지 않음</option>{UNABLE_REASONS.map((reason) => <option key={reason}>{reason}</option>)}
      </select>
    </label>}
    {value.status === 'issue' && <div className="problem-list">
      {item.problems.map((problem, problemIndex) => {
        const selected = Boolean(value.selectedProblems[problem]);
        return <article className={`problem-card ${selected ? 'selected' : ''}`} id={`problem-${item.id}-${problemIndex}`} key={problem}>
          <label className="problem-check"><input type="checkbox" checked={selected} onChange={(e) => toggleProblem(problem, e.target.checked)} /><strong>{problem}</strong></label>
          {selected && <ProblemEditor evalId={item.id} name={problem} state={value.selectedProblems[problem]} onChange={(next) => updateProblem(problem, next)} />}
        </article>;
      })}
    </div>}
  </section>;
}

function CopyCell({ label, text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else {
        const area = document.createElement('textarea');
        area.value = text;
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        area.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      window.alert('복사하지 못했습니다. 텍스트를 직접 선택해 주세요.');
    }
  };
  const onKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      copy();
    }
  };
  return <div className="result-copy-cell" role="button" tabIndex="0" onClick={copy} onKeyDown={onKeyDown} aria-label={`${label} 복사`}>
    <strong>{label}</strong><span>{text}</span><button type="button" className="copy-under-button" onClick={(event) => { event.stopPropagation(); copy(); }}>{copied ? '복사됨' : '복사'}</button>
  </div>;
}

function getPlannedGroups(sections) {
  return EVAL_DATA.map((item) => {
    const section = sections[item.id];
    if (!section || section.status !== 'issue') return null;
    const problems = Object.entries(section.selectedProblems || []).map(([problemName, problem]) => {
      const services = (problem.services || []).filter((service) => service.goal?.trim() && (service.details || []).some((detail) => detail.text?.trim()));
      return services.length > 0 ? { problemName, services } : null;
    }).filter(Boolean);
    return problems.length > 0 ? { item, problems } : null;
  }).filter(Boolean);
}

async function downloadReport(format, subject, sections, version) {
  const token = sessionStorage.getItem('dca_access_token');
  try {
    if (!token) throw new Error('서버 로그인 정보가 없습니다.');
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000/api'}/reports/${format}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ subject: { registrationNo: subject.managementNo, name: maskName(subject.name) }, plan: { version, planData: { sections } } }),
    });
    if (!response.ok) throw new Error('보고서 서버에 연결하지 못했습니다.');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `사례관리_결과보고서_v${version}.${format}`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    if (format === 'pdf') {
      window.alert('테스트 서버가 실행되지 않아 브라우저 인쇄 화면을 엽니다. 대상 프린터에서 PDF 저장을 선택하세요.');
      window.print();
      return;
    }
    const html = `<!doctype html><meta charset="utf-8"><h1>맞춤형 사례관리 결과보고서</h1><p>안심등록번호: ${subject.managementNo}</p><p>대상자: ${maskName(subject.name)}</p>${getPlannedGroups(sections).map(({ item, problems }) => `<h2>${item.name}</h2>${problems.map(({ problemName, services }) => `<h3>${problemName}</h3>${services.map((service) => `<p><b>${service.code}</b></p><p>목표: ${service.goal}</p><p>내용: ${service.details.map((detail) => detail.text).join('<br>')}</p>`).join('')}`).join('')}`).join('')}`;
    const url = URL.createObjectURL(new Blob([html], { type: 'application/msword;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `사례관리_결과보고서_v${version}.doc`;
    link.click();
    URL.revokeObjectURL(url);
  }
}

function ReviewPage({ user, subject, sections, onBack, onSave, onEdit, readOnly = false, savedAt = '', version = '' }) {
  const groups = getPlannedGroups(sections);
  const planCount = groups.reduce((total, group) => total + group.problems.reduce((subtotal, problem) => subtotal + problem.services.length, 0), 0);
  return <main className="content review-page">
    <div className="page-title"><div><p className="eyebrow">{readOnly ? '최종 결과' : '최종 검토'}</p><h1>{maskName(subject.name)} 사례관리 계획</h1></div><button type="button" className="ghost" onClick={onBack}>{readOnly ? '목록으로' : '작성 화면으로'}</button></div>
    <section className="subject-summary">
      <strong>안심등록번호</strong> {subject.managementNo}<strong>대상자</strong> {maskName(subject.name)}<strong>작성자</strong> {user.name}<strong>서비스</strong> {planCount}건
      {version && <><strong>버전</strong> v{version}</>}{savedAt && <><strong>저장일시</strong> {new Date(savedAt).toLocaleString('ko-KR')}</>}
    </section>
    {groups.length === 0 ? <section className="list-card"><p className="empty">목표와 세부내용이 입력된 개입 계획이 없습니다.</p></section> : groups.map(({ item, problems }) => <section className="review-section compact-review" key={item.id}>
      <h2>{item.id}. {item.name}</h2>
      {problems.map(({ problemName, services }) => <div className="review-problem" key={problemName}>
        <h3>{problemName}</h3>
        {services.map((service) => {
          const detailText = service.details.filter((detail) => detail.text?.trim()).map((detail, index) => `${index + 1}. ${detail.text}`).join('\n');
          return <div className="result-grid" key={service.id}>
            <CopyCell label="서비스" text={service.code} />
            <CopyCell label="목표" text={service.goal} />
            <CopyCell label="내용" text={detailText} />
          </div>;
        })}
      </div>)}
    </section>)}
    <div className="bottom-actions">
      <button type="button" className="ghost" onClick={onBack}>{readOnly ? '목록으로' : '수정하기'}</button>
      {readOnly && onEdit && <button type="button" className="secondary" onClick={onEdit}>이 기록 수정</button>}
      {readOnly && <button type="button" className="ghost" onClick={() => downloadReport('pdf', subject, sections, version || 1)}>PDF 보고서</button>}
      {readOnly && <button type="button" className="ghost" onClick={() => downloadReport('docx', subject, sections, version || 1)}>Word 보고서</button>}
      {!readOnly && <button type="button" className="primary" onClick={onSave}>최종 저장</button>}
    </div>
  </main>;
}

function groupRecordsBySubject(records) {
  const groups = new Map();
  records.forEach((record) => {
    const key = record.subject?.managementNo || record.id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(record);
  });
  return [...groups.entries()].map(([key, versions]) => ({
    key,
    versions: versions.sort((a, b) => (Number(b.version) || 0) - (Number(a.version) || 0) || new Date(b.savedAt) - new Date(a.savedAt)),
  })).sort((a, b) => new Date(b.versions[0].savedAt) - new Date(a.versions[0].savedAt));
}

function Records({ records, currentUser, onBack, onNew, onView, onEdit, onDelete, onExport }) {
  const [scope, setScope] = useState('all');
  const [selectedBySubject, setSelectedBySubject] = useState({});
  const visibleRecords = scope === 'mine'
    ? records.filter((record) => record.user?.name === currentUser.name && record.user?.institution === currentUser.institution)
    : records;
  const groups = groupRecordsBySubject(visibleRecords);
  return <main className="content records-page">
    <div className="page-title"><div><p className="eyebrow">담당자 기록 관리</p><h1>최종 저장 목록</h1></div><div className="page-actions"><button type="button" className="primary" onClick={onNew}>+ 새 사례 작성</button><button type="button" className="secondary" disabled={records.length === 0} onClick={onExport}>CSV 내보내기</button><button type="button" className="ghost" onClick={onBack}>작성 화면</button></div></div>
    <div className="record-scope" role="group" aria-label="담당자 범위"><button type="button" className={scope === 'all' ? 'active' : ''} onClick={() => setScope('all')}>전체 담당자 기록</button><button type="button" className={scope === 'mine' ? 'active' : ''} onClick={() => setScope('mine')}>내 기록</button></div>
    <section className="list-card">{groups.length === 0 ? <p className="empty">조건에 맞는 최종 기록이 없습니다.</p> : groups.map(({ key, versions }) => {
      const selectedId = selectedBySubject[key];
      const selected = versions.find((record) => record.id === selectedId) || versions[0];
      return <article className="record-row" key={key}>
        <div><strong>{maskName(selected.subject?.name)}</strong><span>{selected.subject?.managementNo || '안심등록번호 없음'} · {selected.user?.institution || '기관 미입력'}</span><small>작성자 {selected.user?.name || '-'} · 서비스 {countServices(selected.sections || {})}건</small></div>
        <div className="record-meta">
          {versions.length > 1 ? <label className="version-picker">버전
            <select value={selected.id} onChange={(event) => setSelectedBySubject((previous) => ({ ...previous, [key]: event.target.value }))}>{versions.map((record) => <option value={record.id} key={record.id}>v{record.version} · {new Date(record.savedAt).toLocaleDateString('ko-KR')}</option>)}</select>
          </label> : <span>v{selected.version}</span>}
          <small>{new Date(selected.savedAt).toLocaleString('ko-KR')}</small>
          <div><button type="button" className="ghost small" onClick={() => onView(selected)}>보기</button><button type="button" className="secondary small" onClick={() => onEdit(selected)}>수정</button><button type="button" className="danger-link" onClick={() => onDelete(selected.id)}>삭제</button></div>
        </div>
      </article>;
    })}</section>
    <p className="admin-note">현재 시제품에서는 전체 담당자 기록을 한 화면에서 볼 수 있습니다. 실제 운영 시에는 관리자 권한과 기관 범위를 서버에서 검증해야 합니다.</p>
  </main>;
}

function escapeCsv(value) {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

function exportRecords(records) {
  const header = ['기록ID', '버전', '저장일시', '기관', '작성자', '안심등록번호', '대상자(마스킹)', '평가영역', '문제항목', '개입여부', '미개입사유', '서비스', '목표', '세부내용'];
  const rows = [header];
  records.forEach((record) => {
    EVAL_DATA.forEach((item) => {
      const section = record.sections?.[item.id];
      Object.entries(section?.selectedProblems || {}).forEach(([problemName, problem]) => {
        if (problem.intervention === 'no' || !problem.services?.length) {
          rows.push([record.id, record.version, record.savedAt, record.user?.institution, record.user?.name, record.subject?.managementNo, maskName(record.subject?.name), item.name, problemName, '미개입', problem.noReason, '', '', '']);
          return;
        }
        problem.services.forEach((service) => service.details.forEach((detail) => rows.push([record.id, record.version, record.savedAt, record.user?.institution, record.user?.name, record.subject?.managementNo, maskName(record.subject?.name), item.name, problemName, '개입', '', service.code, service.goal, detail.text])));
      });
    });
  });
  const csv = `\uFEFF${rows.map((row) => row.map(escapeCsv).join(',')).join('\r\n')}`;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `맞춤형_사례관리_최종목록_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function App({ initialUser = null, onExit = null }) {
  const [user, setUser] = useState(initialUser);
  const [page, setPage] = useState('form');
  const [subject, setSubject] = useState(emptySubject);
  const [sections, setSections] = useState(makeInitialSections);
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [saveState, setSaveState] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => { api.listRecords().then(setRecords).catch(() => setNotice('저장 목록을 불러오지 못했습니다.')); }, []);
  useEffect(() => {
    if (!user || page !== 'form') return undefined;
    const timer = setTimeout(async () => {
      try {
        setSaveState('저장 중…');
        await api.saveDraft({ user, subject, sections });
        setSaveState('임시저장됨');
      } catch {
        setSaveState('저장 실패');
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, [user, subject, sections, page]);

  const handleLogin = async (credentials) => {
    const nextUser = await api.login(credentials);
    const draft = await api.loadDraft();
    setUser(nextUser);
    if (draft?.user?.name === nextUser.name && draft?.user?.institution === nextUser.institution && draft.subject && draft.sections) {
      setSubject(draft.subject);
      setSections(draft.sections);
      setSaveState('임시저장 복원됨');
      setNotice(`${new Date(draft.savedAt).toLocaleString('ko-KR')}에 저장한 내용을 불러왔습니다.`);
    }
  };

  if (!user) return <Login onLogin={handleLogin} />;

  const validationError = (message, targetId) => ({ message, targetId });
  const validate = () => {
    if (!subject.managementNo.trim() || !subject.name.trim()) return validationError('안심등록번호와 대상자 성명을 입력해 주세요.', 'subject-info');
    for (const item of EVAL_DATA.filter((entry) => entry.req)) {
      const section = sections[item.id];
      if (!section.status) return validationError(`${item.name}의 평가상태를 선택해 주세요.`, `section-${item.id}`);
      if (section.status === 'issue' && Object.keys(section.selectedProblems).length === 0) return validationError(`${item.name}에서 문제항목을 하나 이상 선택해 주세요.`, `section-${item.id}`);
    }
    for (const item of EVAL_DATA) {
      const section = sections[item.id];
      if (section.status !== 'issue') continue;
      for (const [problemName, problem] of Object.entries(section.selectedProblems)) {
        const problemIndex = item.problems.indexOf(problemName);
        if (problem.intervention === 'yes' && problem.services.length === 0) return validationError(`${problemName}에 서비스를 하나 이상 선택해 주세요.`, `problem-${item.id}-${problemIndex}`);
        for (const service of problem.services) {
          if (!service.goal.trim()) return validationError(`${service.code}의 목표를 입력해 주세요.`, `service-${service.id}`);
          if (service.details.length === 0) return validationError(`${service.code}의 세부내용을 입력해 주세요.`, `service-${service.id}`);
          const emptyDetail = service.details.find((detail) => !detail.text.trim());
          if (emptyDetail) return validationError(`${service.code}의 세부내용을 입력해 주세요.`, `detail-${emptyDetail.id}`);
        }
      }
    }
    return null;
  };
  const showValidationError = (error) => {
    window.alert(error.message);
    setTimeout(() => document.getElementById(error.targetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
  };
  const manualSave = async () => {
    try {
      setSaveState('저장 중…');
      await api.saveDraft({ user, subject, sections });
      setSaveState('임시저장됨');
      setNotice('현재 작성 내용을 안전하게 임시저장했습니다.');
    } catch {
      setSaveState('저장 실패');
      window.alert('임시저장에 실패했습니다. 브라우저 저장공간을 확인해 주세요.');
    }
  };
  const goReview = () => {
    const error = validate();
    if (error) return showValidationError(error);
    setPage('review');
    window.scrollTo(0, 0);
  };
  const saveFinal = async () => {
    const error = validate();
    if (error) return showValidationError(error);
    try {
      const result = await api.saveFinal({ user, subject, sections, savedAt: new Date().toISOString() });
      if (result.unchanged) {
        window.alert('수정된 내용이 없습니다. 새 버전을 추가하지 않았습니다.');
        return;
      }
      const nextRecords = await api.listRecords();
      setRecords(nextRecords);
      setSelectedRecord(result.record);
      setEditingRecord(null);
      setNotice(`v${result.record.version}으로 최종 저장되었습니다.`);
      setPage('record');
    } catch {
      window.alert('최종 저장에 실패했습니다. 다시 시도해 주세요.');
    }
  };
  const deleteRecord = async (id) => {
    if (!window.confirm('선택한 버전을 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.')) return;
    await api.deleteRecord(id);
    setRecords(await api.listRecords());
  };
  const editRecord = (record) => {
    setSubject(JSON.parse(JSON.stringify(record.subject)));
    setSections(JSON.parse(JSON.stringify(record.sections)));
    setEditingRecord(record);
    setSelectedRecord(record);
    setSaveState(`v${record.version} 수정 중`);
    setNotice(`${maskName(record.subject?.name)}님의 v${record.version} 기록을 수정하고 있습니다.`);
    setPage('form');
    window.scrollTo(0, 0);
  };
  const newForm = async () => {
    await api.clearDraft();
    setSubject(emptySubject);
    setSections(makeInitialSections());
    setEditingRecord(null);
    setSelectedRecord(null);
    setSaveState('');
    setNotice('새 사례 작성을 시작했습니다.');
    setPage('form');
    window.scrollTo(0, 0);
  };
  const updateSection = (id, next) => setSections((previous) => ({ ...previous, [id]: next }));

  if (page === 'review') return <ReviewPage user={user} subject={subject} sections={sections} onBack={() => setPage('form')} onSave={saveFinal} />;
  if (page === 'record' && selectedRecord) return <ReviewPage user={selectedRecord.user} subject={selectedRecord.subject} sections={selectedRecord.sections} savedAt={selectedRecord.savedAt} version={selectedRecord.version} readOnly onBack={() => setPage('records')} onEdit={() => editRecord(selectedRecord)} />;
  if (page === 'records') return <Records records={records} currentUser={user} onBack={() => setPage('form')} onNew={newForm} onView={(record) => { setSelectedRecord(record); setPage('record'); }} onEdit={editRecord} onDelete={deleteRecord} onExport={() => exportRecords(records)} />;

  const completed = EVAL_DATA.filter((item) => sections[item.id].status).length;
  const scrollToSection = (event, id) => {
    event.preventDefault();
    document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  return <div className="app-shell">
    <aside className="sidebar">
      <div><p className="eyebrow">사례관리 지원</p><h2>평가 항목</h2><p className="sidebar-progress">{completed} / {EVAL_DATA.length} 영역 확인</p></div>
      <nav>{EVAL_DATA.map((item) => <a key={item.id} href={`#section-${item.id}`} onClick={(event) => scrollToSection(event, item.id)} className={sections[item.id].status ? 'done' : ''}><span>{item.id}</span><span>{item.name}{item.req && <em> (필수)</em>}</span>{sections[item.id].status && <b>✓</b>}</a>)}</nav>
    </aside>
    <main className="content">
      <header className="topbar"><div><strong>{user.name} 담당자</strong><span>{user.institution}</span></div><div><span className={`save-state ${saveState === '저장 실패' ? 'save-error' : ''}`}>{saveState}</span><button type="button" className="ghost small" onClick={() => setPage('records')}>저장 목록</button><button type="button" className="ghost small" onClick={() => { if (onExit) onExit(); else setUser(null); setPage('form'); }}>{onExit ? '업무 홈' : '로그아웃'}</button></div></header>
      {notice && <div className="notice-banner" role="status"><span>{notice}</span><button type="button" onClick={() => setNotice('')} aria-label="알림 닫기">×</button></div>}
      <div className="page-title"><div><p className="eyebrow">맞춤형 사례관리</p><h1>{editingRecord ? `저장 기록 수정 · v${editingRecord.version}` : '대상자 평가 및 서비스 계획'}</h1></div><button type="button" className="ghost" onClick={newForm}>새 사례 작성</button></div>
      <section className="subject-card" id="subject-info"><h2>대상자 기본정보</h2><div className="two-col"><label>안심등록번호<input value={subject.managementNo} onChange={(e) => setSubject({ ...subject, managementNo: e.target.value })} placeholder="안심등록번호" /></label><label>대상자 성명<input value={subject.name} onChange={(e) => setSubject({ ...subject, name: e.target.value })} placeholder="홍길동" autoComplete="off" /></label></div><p className="muted">목록과 최종 검토 화면에서는 성명이 마스킹됩니다. 주민등록번호와 연락처는 입력하지 마세요.</p></section>
      {EVAL_DATA.map((item) => <EvaluationSection key={item.id} item={item} value={sections[item.id]} onChange={(next) => updateSection(item.id, next)} />)}
      <div className="bottom-actions"><button type="button" className="secondary" onClick={manualSave}>임시저장</button><button type="button" className="primary" onClick={goReview}>전체 내용 검토</button></div>
    </main>
  </div>;
}

export default App;
